import {
  Badge,
  Center,
  NavLink as MantineNavLink,
  rem,
  Title,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconProps,
} from "@tabler/icons-react";
import { NavLink, useParams } from "react-router-dom";

const useStyles = createStyles((theme) => ({
  navbar: {
    paddingTop: 0,
  },

  section: {
    marginLeft: `calc(${theme.spacing.md} * -1)`,
    marginRight: `calc(${theme.spacing.md} * -1)`,
    marginBottom: theme.spacing.md,

    "&:not(:last-of-type)": {
      borderBottom: `${rem(1)} solid ${theme.colors.dark[4]}`,
    },
  },

  searchCode: {
    fontWeight: 700,
    fontSize: rem(10),
    backgroundColor: theme.colors.dark[7],
    border: `${rem(1)} solid ${theme.colors.dark[7]}`,
  },

  mainLinks: {
    paddingLeft: `calc(${theme.spacing.md} - ${theme.spacing.xs})`,
    paddingRight: `calc(${theme.spacing.md} - ${theme.spacing.xs})`,
    paddingBottom: theme.spacing.md,
  },

  mainLink: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    fontSize: theme.fontSizes.xs,
    padding: `${rem(8)} ${theme.spacing.xs}`,
    borderRadius: theme.radius.sm,
    fontWeight: 500,
    color: theme.colors.dark[0],

    "&:hover": {
      backgroundColor: theme.colors.dark[6],
      color: theme.white,
    },
  },

  mainLinkActive: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    fontSize: theme.fontSizes.xs,
    padding: `${rem(8)} ${theme.spacing.xs}`,
    borderRadius: theme.radius.sm,
    fontWeight: 500,

    backgroundColor: theme.colors.dark[6],
    color: theme.white,
  },

  mainLinkInner: {
    display: "flex",
    alignItems: "center",
    flex: 1,
  },

  mainLinkIcon: {
    marginRight: theme.spacing.sm,
    color: theme.colors.dark[2],
  },

  mainLinkBadge: {
    padding: 0,
    width: rem(20),
    height: rem(20),
    pointerEvents: "none",
  },

  links: {
    marginLeft: `calc(${theme.spacing.md} * -1)`,
    marginRight: `calc(${theme.spacing.md} * -1)`,
  },

  linksInner: {
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
  },
}));

type NavbarItem = {
  icon?: React.ComponentType<IconProps>;
  label: string;
  notifications?: number;
  path?: string;
  disabled?: boolean;
  children?: NavbarItem[];
};

export interface NavbarConfiguration {
  title: string;
  items: NavbarItem[];
}

const VARS_REGEXP = /:([a-zA-Z0-9_-]+)/g;

export function NavbarSearch({
  configuration: { items, title },
  onLinkClick,
}: {
  configuration: NavbarConfiguration;
  onLinkClick?: () => void;
}) {
  const params = useParams();
  const { classes } = useStyles();

  const mainLinks = items.map((link) => (
    <NavLink
      to={
        link.path?.replaceAll(VARS_REGEXP, (m, i) =>
          i in params ? (params[i] as string) : m,
        ) ?? ""
      }
      key={link.path}
      onClick={(e) => {
        if (link.disabled) {
          e.preventDefault();
        }
        if (link.path) {
          onLinkClick?.();
        }
      }}
      end
    >
      {({ isActive }) => (
        <MantineNavLink
          disabled={link.disabled}
          className={classes.mainLink}
          active={isActive && !link.children?.length}
          label={link.label}
          leftSection={
            link.icon && (
              <link.icon
                size={20}
                className={classes.mainLinkIcon}
                stroke={1.5}
              />
            )
          }
          rightSection={
            <>
              {link.notifications && (
                <Badge
                  size="sm"
                  variant="filled"
                  className={classes.mainLinkBadge}
                >
                  {link.notifications}
                </Badge>
              )}{" "}
              {link.children && <IconChevronRight />}
            </>
          }
        >
          {link.children &&
            link.children.map((link) => (
              <NavLink
                to={
                  link.path?.replaceAll(VARS_REGEXP, (m, i) =>
                    i in params ? (params[i] as string) : m,
                  ) ?? ""
                }
                key={link.path}
                onClick={(e) => {
                  if (link.disabled || link.children?.length) {
                    e.preventDefault();
                  }
                  if (link.path) {
                    onLinkClick?.();
                  }
                }}
                end
              >
                {({ isActive }) => (
                  <MantineNavLink
                    disabled={link.disabled}
                    className={classes.mainLink}
                    active={isActive}
                    label={link.label}
                    leftSection={
                      link.icon && (
                        <link.icon
                          size={20}
                          className={classes.mainLinkIcon}
                          stroke={1.5}
                        />
                      )
                    }
                    rightSection={
                      <>
                        {link.notifications && (
                          <Badge
                            size="sm"
                            variant="filled"
                            className={classes.mainLinkBadge}
                          >
                            {link.notifications}
                          </Badge>
                        )}
                      </>
                    }
                  />
                )}
              </NavLink>
            ))}
        </MantineNavLink>
      )}
    </NavLink>
  ));

  return (
    <nav
      className={classes.navbar}
      style={{
        position: "sticky",
        top: 60,
        height: "calc(100vh - 60px)",
        width: 300,
        minWidth: 300,
        padding: rem(16),
        backgroundColor: "var(--mantine-color-dark-7)",
        borderRight: "1px solid var(--mantine-color-dark-4)",
        overflowY: "auto",
      }}
    >
      <div className={classes.section}>
        <Center>
          <Title>{title}</Title>
        </Center>
      </div>
      <div className={classes.section}>
        <div className={classes.mainLinks}>{mainLinks}</div>
      </div>
    </nav>
  );
}
